"use client";
import React, { useEffect, useState, useMemo } from "react";
import StepEmail from "./_steps/step-email";
import StepSalary from "./_steps/step-salary";
import StepOtp from "./_steps/step-otp";
import StepDeviceSelection from "./_steps/step-device-selection";
import { useAppData, useAppStore } from "@/store/appStore";
import StepUserInfo from "./_steps/step-user-info";
import StepMobile from "./_steps/step-mobile";
import StepMobileOtp from "./_steps/step-mobile-otp";
import { updateUserData } from "@/app/product/_actions/user.actions";
import { trackEvent, GTMEvent, getUTMFromURL } from "@/lib/utils/gtm-events";
import { CookieConsent } from "@/components/cookie-consent/CookieConsent";
import { LandingPanel } from "./_landing/LandingPanel";

enum OnboardingStep {
	EMAIL = "EMAIL",
	EMAIL_OTP = "EMAIL_OTP",
	MOBILE = "MOBILE",
	MOBILE_OTP = "MOBILE_OTP",
	USER_INFO = "USER_INFO",
	SALARY = "SALARY",
	DEVICE_SELECTION = "DEVICE_SELECTION",
}

export interface OnBoardingProps {
	onComplete: (selection?: "cars" | "devices") => void;
	currentStep: number;
	onNext: (stepName?: string) => void;
}

const OnBoarding: React.FC<OnBoardingProps> = ({
	onComplete,
	currentStep,
	onNext,
}) => {
	const [appData, setAppData] = useAppData();
	const [mobileOtpTarget, setMobileOtpTarget] = useState<string>();
	const [mobileLandingDismissed, setMobileLandingDismissed] = useState(false);

	// No CTC capture for this company - the salary step should never be
	// entered; every branch that would otherwise route to "salary" routes
	// straight to whatever the salary step itself would go to next.
	const showCtc = appData?.showCtc !== false;
	const getPostSalaryStep = () => {
		const isGuest = !appData?.user;
		const hasName = appData?.user?.firstName && appData?.user?.lastName;
		return isGuest || hasName ? "device-selection" : "user-info";
	};

	useEffect(() => {
		const prevOverflow = document.body.style.overflow;
		document.body.style.overflow = "hidden";
		return () => {
			document.body.style.overflow = prevOverflow;
		};
	}, []);

	// Track onboarding visited event — retry until CleverTap is ready
	useEffect(() => {
		if (
			typeof window === "undefined" ||
			sessionStorage.getItem("onboarding_visited")
		)
			return;
		const interval = setInterval(() => {
			if (window.clevertap) {
				trackEvent(GTMEvent.ONBOARDING_VISITED, {
					...getUTMFromURL(),
				});
				sessionStorage.setItem("onboarding_visited", "true");
				clearInterval(interval);
			}
		}, 300);
		return () => clearInterval(interval);
	}, []);

	// Once user moves past email step, mobile landing should stay dismissed
	useEffect(() => {
		if (currentStep > 0 && !mobileLandingDismissed) {
			setMobileLandingDismissed(true);
		}
	}, [currentStep, mobileLandingDismissed]);

	const stepContentMapping = [
		OnboardingStep.EMAIL,
		OnboardingStep.EMAIL_OTP,
		OnboardingStep.MOBILE,
		OnboardingStep.MOBILE_OTP,
		OnboardingStep.SALARY,
		OnboardingStep.USER_INFO,
		OnboardingStep.DEVICE_SELECTION,
	];

	const stepContent = useMemo(() => {
		const stepKey =
			currentStep < stepContentMapping.length
				? stepContentMapping[currentStep]
				: OnboardingStep.EMAIL;

		switch (stepKey) {
			case OnboardingStep.EMAIL:
				return (
					<StepEmail
						onSubmit={async () => {
							onNext();
						}}
						onSkip={() => {
							onNext(showCtc ? "salary" : getPostSalaryStep());
						}}
						onOAuthSuccess={() => {
							const latestAppData = useAppStore.getState().data;
							const latestShowCtc = latestAppData?.showCtc !== false;

							if (latestAppData?.isExistingUser) {
								if (
									!latestAppData?.user?.firstName ||
									!latestAppData?.user?.lastName ||
									(!latestAppData?.hasAcceptedOnBoardingConsent &&
										latestAppData?.isOnboardingConsentEnabled)
								) {
									onNext("user-info");
								} else {
									onNext("device-selection");
								}
							} else if (!latestAppData?.phoneNumber) {
								onNext("mobile");
							} else if (!latestAppData?.user?.firstName) {
								onNext("user-info");
							} else if (latestShowCtc && !latestAppData?.ctc) {
								onNext("salary");
							} else {
								onNext("device-selection");
							}
						}}
					/>
				);
			case OnboardingStep.EMAIL_OTP:
				return (
					<StepOtp
						onSubmit={(nextStep?: string) => {
							onNext(nextStep);
						}}
						onSkip={() => {
							onNext(showCtc ? "salary" : getPostSalaryStep());
						}}
						onBack={() => {
							onNext("email");
						}}
						emailForOtp={appData.email}
					/>
				);
			case OnboardingStep.MOBILE:
				return (
					<StepMobile
						onContinue={(mobile) => {
							setMobileOtpTarget(mobile);
							onNext();
						}}
						onSkip={() => {
							if (appData.isExistingUser) {
								onNext(!showCtc || appData.ctc ? "device-selection" : "salary");
							} else {
								onNext(showCtc ? "salary" : getPostSalaryStep());
							}
						}}
					/>
				);
			case OnboardingStep.MOBILE_OTP:
				return (
					<StepMobileOtp
						onSubmit={() => {
							if (appData.isExistingUser) {
								onNext(!showCtc || appData.ctc ? "device-selection" : "salary");
							} else {
								onNext();
							}
						}}
						onSkip={() => {
							if (appData.isExistingUser) {
								onNext(!showCtc || appData.ctc ? "device-selection" : "salary");
							} else {
								onNext(showCtc ? "salary" : getPostSalaryStep());
							}
						}}
						onBack={() => {
							setMobileOtpTarget(undefined);
							onNext("mobile");
						}}
						mobileForOtp={mobileOtpTarget}
					/>
				);
			case OnboardingStep.USER_INFO:
				return (
					<StepUserInfo
						onSubmit={() => {
							onNext("device-selection");
						}}
					/>
				);

			case OnboardingStep.SALARY:
				return (
					<StepSalary
						initialSalary={appData?.ctc || undefined}
						onSubmit={async function (salary: number): Promise<void> {
							setAppData({ ctc: salary });
							try {
								await updateUserData({ ctc: salary });
							} catch {
								// Continue even if backend update fails
							}
							await new Promise((resolve) => setTimeout(resolve, 0));
							onNext(getPostSalaryStep());
						}}
						onSkip={() => {
							onNext(getPostSalaryStep());
						}}
					/>
				);
			case OnboardingStep.DEVICE_SELECTION:
				return (
					<StepDeviceSelection
						onComplete={(selection) => {
							onComplete(selection);
						}}
					/>
				);
			default:
				return null;
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [
		currentStep,
		appData.email,
		mobileOtpTarget,
		appData.isExistingUser,
		appData.ctc,
		appData.showCtc,
		appData.user,
	]);

	// Defense-in-depth: if CTC capture is disabled but the salary step is
	// somehow reached anyway (e.g. a stale deep link), skip straight past it.
	useEffect(() => {
		if (!showCtc && stepContentMapping[currentStep] === OnboardingStep.SALARY) {
			onNext(getPostSalaryStep());
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [showCtc, currentStep]);

	const isEmailStep = currentStep === 0;
	const showMobileLanding = isEmailStep && !mobileLandingDismissed;

	return (
		<>
			<CookieConsent />
			<div className="onboarding-button-vars fixed inset-0 z-[100] overflow-y-auto bg-[var(--automint-onboarding-bg)]">
				{/* Mobile layout */}
				<div className="md:hidden h-full w-full">
					{showMobileLanding ? (
						<div className="relative h-full w-full flex flex-col bg-[var(--automint-onboarding-bg)]">
							<div className="flex-1 overflow-y-auto px-5 pt-6 pb-[231px] [&::-webkit-scrollbar]:hidden [scrollbar-width:none]">
								<LandingPanel showMobileLogo />
							</div>

							<div className="pointer-events-none absolute inset-x-0 bottom-0 z-20 h-[231px] flex flex-col items-center justify-center gap-3 px-4 bg-gradient-to-b from-transparent to-[var(--automint-onboarding-bg)]">
								<button
									type="button"
									onClick={() => setMobileLandingDismissed(true)}
									className="pointer-events-auto w-full h-12 rounded-[6px] bg-automint-black text-automint-neon font-semibold text-[15px]"
								>
									Login
								</button>
								<button
									type="button"
									onClick={() =>
										onNext(showCtc ? "salary" : getPostSalaryStep())
									}
									className="pointer-events-auto text-[var(--automint-black)] text-sm underline underline-offset-4 hover:opacity-80"
								>
									Skip
								</button>
							</div>
						</div>
					) : (
						<div className="h-full w-full overflow-y-auto bg-white">
							{stepContent}
						</div>
					)}
				</div>

				<div className="hidden md:flex w-full min-h-full items-center justify-center px-4 lg:px-6 py-4 lg:py-5">
					<div className="grid grid-cols-[7fr_3fr] w-full max-w-[1600px] h-[calc(100vh-2rem)] lg:h-[calc(100vh-2.5rem)] max-h-[1000px] 2xl:max-h-[1100px] gap-4 lg:gap-6">
						<div className="min-w-0 flex justify-center h-full">
							<div className="max-w-[680px] w-full h-full mx-auto">
								<LandingPanel />
							</div>
						</div>
						<div className="min-w-0 h-full">{stepContent}</div>
					</div>
				</div>
			</div>
		</>
	);
};

export default OnBoarding;
